import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { REGISTERS, REGISTER_ORDER, type PostType } from '@/features/posts/registers';
import type { Pulse } from '@/lib/schemas/profile';
import { radius } from '@/theme';

interface PulseBarProps {
  pulse: Pulse;
  /** Names the subject in the accessible summary: "Alina Berce", "Timișoara this week". */
  subject: string;
  /**
   * Percentages to use instead of computing them from the counts.
   *
   * The Town Pulse endpoint nudges its three shares to add up to exactly 100
   * (spec §8.1); rounding the same counts a second time here is how a card ends
   * up reading "50%, 30%, 21%". The *sentence* is still written here, because
   * it has to be in the reader's language (spec §8.9).
   */
  percentages?: { good: number; bad: number; ideal: number };
  /** Overrides the whole sentence — used for "nothing posted yet" phrasing. */
  emptyText?: string;
  height?: number;
  showLegend?: boolean;
}

const SEGMENT_KEY: Record<PostType, keyof Pick<Pulse, 'good' | 'bad' | 'ideal'>> = {
  GOOD: 'good',
  BAD: 'bad',
  IDEAL: 'ideal',
};

/**
 * The three-segment good/bad/ideal bar (spec §9.4, and §8.1's Town Pulse).
 *
 * The bar itself is decorative: it is hidden from assistive technology and the
 * same information is given as a sentence, because a screen-reader user should
 * hear "62% good, 24% bad, 14% ideal" rather than three unlabelled regions.
 */
export function PulseBar({
  pulse,
  subject,
  percentages: provided,
  emptyText,
  height = 10,
  showLegend = true,
}: PulseBarProps) {
  const { t } = useTranslation();

  if (pulse.total === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyText ?? t('pulse.empty')}
      </Typography>
    );
  }

  const percentages = REGISTER_ORDER.map((type) => ({
    type,
    count: pulse[SEGMENT_KEY[type]],
    percent:
      provided?.[SEGMENT_KEY[type]] ?? Math.round((pulse[SEGMENT_KEY[type]] / pulse.total) * 100),
  }));

  const summary = percentages
    .filter((segment) => segment.count > 0)
    .map(
      (segment) =>
        `${segment.percent}% ${t(`registers.${segment.type}.label`).toLocaleLowerCase()}`,
    )
    .join(', ');

  return (
    <Stack spacing={1}>
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          height,
          borderRadius: `${radius.pill}px`,
          overflow: 'hidden',
          backgroundColor: 'action.hover',
        }}
      >
        {percentages.map(({ type, count, percent }) =>
          count === 0 ? null : (
            <Tooltip
              key={type}
              title={`${count} ${t(`registers.${type}.label`).toLocaleLowerCase()} · ${percent}%`}
            >
              <Box
                sx={{
                  width: `${percent}%`,
                  backgroundColor: (theme) => theme.palette[REGISTERS[type].accent].main,
                }}
              />
            </Tooltip>
          ),
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('pulse.summary', { subject, summary })}
      </Typography>

      {showLegend && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap aria-hidden>
          {percentages.map(({ type, count }) => {
            const register = REGISTERS[type];
            return (
              <Stack key={type} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: (theme) => theme.palette[register.accent].main,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(`registers.${type}.label`)} {count}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
